// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store @ actions
import useSubscriptionStore from '@/app/store/modules/subscription';
import actionsPosTerminals from '../../../api/actionsPosTerminals';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import BodyMultipleBlockingBySelection from './BodyMultipleBlockingBySelection';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsTerminal } from '@/app/i18n/namespaces';

/**
 *
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @returns {JSX} ModalMultipleBlockingPos
 */
function ModalMultipleBlockingPos({ isOpen, setIsOpen }) {
    // Constants
    const STEPS_NUMBER = 3;

    // Toast
    const { toast } = useToast();

    // Store
    const storeRefetchSubscriptions = useSubscriptionStore(s => s.storeRefetchSubscriptions);

    // State
    const [step, setStep] = useState(0);
    const [disabledSubmit, setDisabledSubmit] = useState(false);
    const [actionPayload, setActionPayload] = useState({});
    const [posTerminalsNotBlacklisted, setPosTerminalsNotBlacklisted] = useState(null);
    const [errorKey, setErrorKey] = useState('');
    const [showPostBlockingErrors, setShowPostBlockingErrors] = useState(false);

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchSubscriptions(true);
        toast({
            description: <Entity entity={'modal.multiple_blocking.success'} namespace={nsTerminal} />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        const subscriptionsErrors = error?.response?.data?.subscriptionsErrors;
        const keys = subscriptionsErrors ? Object.keys(subscriptionsErrors) : '';

        setShowPostBlockingErrors(true);
        setErrorKey(Object.keys(subscriptionsErrors) || '');
        setPosTerminalsNotBlacklisted(subscriptionsErrors[keys[0]] || null);
    };
    const multiplePosBlocking = actionsPosTerminals.multiplePosBlocking(onSuccess, onError);

    // Hooks
    useEffect(() => {
        if (step !== 0) {
            setDisabledSubmit(false);
        }
    }, [step]);

    // Functions
    const handlePosBlocking = () => {
        multiplePosBlocking.mutate(actionPayload);
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step === 0 ? 'max-w-6xl' : 'max-w-3xl'}>
                <HeaderTitle title='modal.multiple_blocking.title' namespace={nsTerminal} />
                {step < STEPS_NUMBER && !showPostBlockingErrors &&
                    <BodyMultipleBlockingBySelection
                        step={step}
                        setDisabledSubmit={setDisabledSubmit}
                        setActionPayload={setActionPayload}
                    />
                }
                {step === 2 && showPostBlockingErrors &&
                    <>
                        <ModalBody>
                            <div className='p-4 flex justify-center space-x-6'>
                                <Entity
                                    entity={errorKey ? 'modal.multiple_blocking.error_with_list1' : 'modal.multiple_blocking.error'}
                                    namespace={nsTerminal}
                                />
                                {
                                    errorKey &&
                                    <div>
                                        <br />
                                        <Entity entity='modal.multiple_blocking.error_with_list2' />
                                        <br /><br />
                                        {
                                            posTerminalsNotBlacklisted.map((serialNumber, index) => {
                                                return <div key={`${serialNumber}-${index}`}>{serialNumber}</div>;
                                            })
                                        }
                                    </div>
                                }
                            </div>
                        </ModalBody>
                    </>
                }
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={handlePosBlocking}
                    isLoading={multiplePosBlocking.isLoading}
                    disableValidation={disabledSubmit}
                    isError={showPostBlockingErrors}
                />
            </ModalContent>
        </Modal>
    );
}

ModalMultipleBlockingPos.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};

export default ModalMultipleBlockingPos;